// @mui
import { Box, Stack, Link, Typography, Container, useMediaQuery } from '@mui/material';
// locales
import { useLocales } from '@locales';
// components
import LanguageSelection from '../../components/minimal/LanguageSelection';
import PowerdByNimi from '../../components/PowerdByNimi';
// hooks
import { useSellerPageContext } from '../../context-state/SellerPageContext';
import { useTermsFormsForAccount } from '../../hooks/useTermsForms';
import { isSafeHttpUrl } from '../../utils/validators';

// ----------------------------------------------------------------------

function buildAddressLine(address) {
    if (!address) return '';
    const street = [address.street, address.streetNumber].filter(Boolean).join(' ').trim();
    return [street, address.city, address.state, address.zipCode, address.country]
        .filter(Boolean)
        .join(', ');
}

const Bullet = () => (
    <Typography component="span" variant="body2" sx={{ color: 'text.disabled' }}>·</Typography>
);

export default function SellerPageFooter() {
    const { accountContext } = useSellerPageContext();
    const { account } = accountContext;
    const { privacyPolicy, termsOfService } = useTermsFormsForAccount(account);
    const { translate } = useLocales();
    const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('sm'));

    const sellerName = account?.name;
    const addressLine = buildAddressLine(account?.address);
    // Owner phone: the public account endpoint may populate `owner` as an object or
    // flatten the phone onto the account root — support both, render nothing if absent.
    const sellerPhone = account?.owner?.phone || account?.phone;
    const companyCode = account?.companyCode;

    const infoParts = [
        sellerName && (
            <Typography
                key="name"
                variant="body2"
                sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    whiteSpace: { sm: 'nowrap' },
                }}
            >
                {sellerName}
            </Typography>
        ),
        addressLine && (
            <Typography
                key="addr"
                variant="body2"
                color="text.secondary"
                sx={{
                    overflow: { sm: 'hidden' },
                    textOverflow: { sm: 'ellipsis' },
                    whiteSpace: { sm: 'nowrap' },
                    textAlign: 'center',
                }}
            >
                {addressLine}
            </Typography>
        ),
        sellerPhone && (
            <Link
                key="phone"
                href={`tel:${sellerPhone}`}
                variant="body2"
                color="text.secondary"
                sx={{
                    whiteSpace: { sm: 'nowrap' },
                    textAlign: 'center',
                    direction: 'ltr',
                }}
            >
                {sellerPhone}
            </Link>
        ),
        companyCode && (
            <Typography
                key="cc"
                variant="body2"
                color="text.disabled"
                sx={{ whiteSpace: { sm: 'nowrap' } }}
            >
                {`${translate('company_code')} ${companyCode}`}
            </Typography>
        ),
    ].filter(Boolean);

    return (
        <Box
            component="footer"
            sx={{
                py: 1.5,
                px: 2,
                mt: 6,
                backgroundColor: 'background.paper',
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            }}
        >
            <Container>
                <Stack spacing={1} alignItems="center">
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={{ xs: 1, sm: 2 }}
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ width: '100%' }}
                    >
                        {infoParts.length > 0 && (
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={{ xs: 0.25, sm: 1.5 }}
                                divider={isDesktop ? <Bullet /> : null}
                                alignItems="center"
                                sx={{ flex: { sm: 1 }, minWidth: 0 }}
                            >
                                {infoParts}
                            </Stack>
                        )}

                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            justifyContent="center"
                        >
                            {privacyPolicy && privacyPolicy !== 'hide' && isSafeHttpUrl(privacyPolicy) && (
                                <Link
                                    href={privacyPolicy}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {translate('privacy_policy')}
                                </Link>
                            )}
                            {termsOfService && termsOfService !== 'hide' && isSafeHttpUrl(termsOfService) && (
                                <Link
                                    href={termsOfService}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {translate('terms_of_service')}
                                </Link>
                            )}
                            <LanguageSelection variant="body2" color="textSecondary" />
                        </Stack>
                    </Stack>

                    <PowerdByNimi />
                </Stack>
            </Container>
        </Box>
    );
}
